// A partner's Users page: the athletes they own, and Add Athlete. Deliberately
// its own component -- AdminUsersPage.js carries delete, bulk-delete, role,
// password and approval controls a partner must never have, so keeping it
// separate makes "no admin control exists here" true by construction instead
// of by a threaded-through mode flag (2026-09-15 partner-role-redesign spec,
// Task 16). Do not import AdminUsersPage or add a partner branch to it.
//
// No route renders this page any more: partners on /admin/users are redirected to Athletes &
// Staff (partner nav redesign, 2026-11-05). Its AddAthleteModal is what that page uses.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCatalog } from '../context/CatalogContext';
import AdminPage from '../components/AdminShell';
import Button from '../components/Button';

const EMPTY = {
  first_name: '', last_name: '', email: '', phone: '', sport: '', grad_year: '',
  g_first: '', g_last: '', g_email: '', g_phone: '',
  // Ticked by default (David, 2026-10-13): a partner adding an athlete normally takes
  // them on as a concierge client. The partner can untick it per athlete.
  enroll_as_concierge: true,
};

// Mirrors the {linked, reason} shape POST /api/partner/athletes returns for
// `guardian`. A conflict or missing-contact result is reported honestly --
// never rendered as if the guardian link succeeded (global constraint: no
// silent success on a guardian conflict).
function guardianMessage(g) {
  if (!g) return '';
  if (g.linked) return 'Guardian linked.';
  if (g.reason === 'conflict') {
    return 'Guardian saved as a contact only — that email or phone belongs to another account.';
  }
  if (g.reason === 'no_contact') {
    return 'Guardian name saved; add an email or phone to link an account.';
  }
  return '';
}

// The Add Athlete modal: POST /api/partner/athletes. Mounted only while open, so every
// opening starts from a blank form. Shared with the Athletes & Staff page (AthletesStaffPage.jsx).
// `onCreated(message)` gets the status line to show once the athlete exists.
export function AddAthleteModal({ onClose, onCreated }) {
  const { API_URL } = useAuth();
  const catalog = useCatalog();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // profile.sport stores the DISPLAY NAME, not the slug (same convention as
  // AdminAthleteEditPanel.js's handleSportChange) -- the select's value is the
  // slug for the control, but what lands in `form.sport`, and therefore in
  // the POST body, is the catalog entry's `name`.
  const currentSportSlug = (catalog.sports.find((s) => s.name === form.sport) || {}).slug || '';
  const handleSportChange = (e) => {
    const slug = e.target.value;
    const next = catalog.sports.find((s) => s.slug === slug);
    setForm((f) => ({ ...f, sport: next ? next.name : '' }));
  };

  const submit = async () => {
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.');
      return;
    }
    const hasGuardian = form.g_first.trim() || form.g_last.trim() || form.g_email.trim() || form.g_phone.trim();
    const body = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      sport: form.sport || null,
      grad_year: form.grad_year ? Number(form.grad_year) : null,
      guardian: hasGuardian ? {
        first_name: form.g_first.trim(),
        last_name: form.g_last.trim(),
        email: form.g_email.trim() || null,
        phone: form.g_phone.trim() || null,
      } : null,
      enroll_as_concierge: !!form.enroll_as_concierge,
    };
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/partner/athletes`, body);
      const gm = guardianMessage(r.data.guardian);
      onCreated([`${r.data.athlete.name} added.`, gm].filter(Boolean).join(' '));
    } catch (err) {
      setSaving(false);
      if (err.response?.status === 409) {
        // The colliding account is never named or hinted at here (global
        // constraint) -- an admin has to make the assignment call.
        setError('That email or phone already has a ProZpects account. Ask an admin to assign the athlete to you.');
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Could not create the athlete. Please try again.');
      }
    }
  };

  const textField = (id, label, key, type = 'text') => (
    <div className="form-field">
      <label className="form-field__label" htmlFor={id}>{label}</label>
      <input id={id} className="form-field__input" type={type} value={form[key]} onChange={set(key)} />
    </div>
  );

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="add-athlete-title" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="add-athlete-title">Add Athlete</div>

        {error && <div role="alert" className="settings-toast settings-toast--error">{error}</div>}

        {textField('pa-first', 'Athlete first name', 'first_name')}
        {textField('pa-last', 'Athlete last name', 'last_name')}
        {textField('pa-email', 'Athlete email', 'email', 'email')}
        {textField('pa-phone', 'Athlete phone', 'phone', 'tel')}

        <div className="form-field">
          <label className="form-field__label" htmlFor="pa-sport">Sport</label>
          <select id="pa-sport" className="form-field__input" value={currentSportSlug} onChange={handleSportChange}>
            <option value="">Select sport…</option>
            {catalog.sports.map((s) => (
              <option key={s.slug} value={s.slug}>{s.name}</option>
            ))}
          </select>
        </div>

        {textField('pa-grad', 'Grad year', 'grad_year', 'number')}

        {textField('pa-gfirst', 'Guardian first name', 'g_first')}
        {textField('pa-glast', 'Guardian last name', 'g_last')}
        {textField('pa-gemail', 'Guardian email', 'g_email', 'email')}
        {textField('pa-gphone', 'Guardian phone', 'g_phone', 'tel')}

        <div className="form-field">
          <label className="form-field__label" htmlFor="pa-concierge">
            <input id="pa-concierge" type="checkbox" checked={form.enroll_as_concierge}
              onChange={(e) => setForm((f) => ({ ...f, enroll_as_concierge: e.target.checked }))} />
            {' '}I will be this athlete’s concierge
          </label>
        </div>

        <p className="ae-hint">
          No email is sent. The family claims the account themselves with “Forgot password”.
        </p>

        <Button variant="accent" onClick={submit} disabled={saving}>
          {saving ? 'Creating…' : 'Create athlete'}
        </Button>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

export default function PartnerUsersPage() {
  const { API_URL } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/partner/athletes`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Could not load your athletes.'))
      .finally(() => setLoading(false));
  }, [API_URL]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => setShowAdd(true);
  // The Partner home's "+ Add athlete" button links here with ?add=1.
  const wantsAdd = searchParams.get('add') === '1';
  useEffect(() => { if (wantsAdd) setShowAdd(true); }, [wantsAdd]);
  const created = (msg) => { setStatus(msg); setShowAdd(false); load(); };

  return (
    <AdminPage
      active="users"
      title="My Athletes"
      subtitle="Athletes you own, and adding a new one."
      actions={<Button variant="accent" onClick={openAdd}>+ Add Athlete</Button>}
    >
      {status && <div role="status" className="settings-toast">{status}</div>}
      {error && <div role="alert" className="settings-toast settings-toast--error">{error}</div>}

      <div className="adm__card">
        {loading ? (
          <div className="adm-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <p>No athletes yet. Add one, or ask an admin to assign an existing athlete to you.</p>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sport</th>
                <th>Grad year</th>
                <th>Concierge</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id}>
                  <td><Link to={`/athletes/${r.user_id}`}>{r.name}</Link></td>
                  <td>{r.sport || '—'}</td>
                  <td>{r.graduation_year || '—'}</td>
                  <td>{r.is_client ? <Link to={`/athletes/${r.user_id}/concierge`}>Client</Link> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddAthleteModal onClose={() => setShowAdd(false)} onCreated={created} />}
    </AdminPage>
  );
}
